import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const FAQS = [
  {
    q: "What is EarlyPrint and how does it work?",
    a: "EarlyPrint is India's complete printing marketplace. Upload your design file, choose your product, and instantly receive live price quotes from multiple verified nearby print vendors. Compare, select, pay securely, and get your prints delivered — all from one screen. No shop visits. No haggling. No wasted time.",
  },
  {
    q: "What types of products can I print on EarlyPrint?",
    a: "Almost anything — visiting cards, brochures, pamphlets, flex boards, LED boards, T-shirts (sublimation), packaging boxes, wedding cards, stickers, books, certificates, posters, letterheads, labels and more. New categories are added regularly.",
  },
  {
    q: "How do I know which vendor to choose?",
    a: "When you place an order request, multiple nearby verified vendors respond with their price, delivery time, and rating. You see each vendor's star rating, reviews, distance from you, and message — side by side. You choose the vendor that best matches your budget, quality expectation, and timeline.",
  },
  {
    q: "Is my payment safe on EarlyPrint?",
    a: "Absolutely. EarlyPrint uses a secure escrow payment system. When you pay, your money is held safely and released to the vendor only after you confirm your order has been delivered and you are satisfied. If there is any issue, your money is refunded to your EarlyPrint wallet. You are never at risk.",
  },
  {
    q: "What file formats are accepted for uploading my design?",
    a: "EarlyPrint accepts all major design and print file formats — PDF, AI (Adobe Illustrator), CDR (CorelDRAW), PSD (Photoshop), PNG, and JPG. For best print quality, upload a PDF or AI file at 300 DPI with proper bleed margins. Our AI file checker automatically scans your file and alerts you if anything needs to be fixed before printing.",
  },
  {
    q: "What is the AI file checker and how does it help me?",
    a: "The AI file checker is an automatic quality scan that runs the moment you upload your design. It checks for resolution (is the image sharp enough to print?), bleed margins (will there be unwanted white edges?), and colour mode (is the file in CMYK for accurate print colours?). If any issue is found, you are told exactly what to fix — before any vendor even sees your file.",
  },
  {
    q: "Can I order a sample before placing a bulk order?",
    a: "Yes — highly recommended for first-time or large orders. Three sample options: a free digital mockup (emailed within 30 minutes), a physical sample delivered to your door for ₹99, or a material swatch kit showing all available paper finishes for ₹49. If you proceed with the bulk order within 7 days, the sample cost is deducted from your total.",
  },
  {
    q: "How long does delivery take?",
    a: "Depends on the product and the vendor you select. Most standard orders are delivered within 2–5 business days. Express (1–2 days) and same-day options are available from select vendors for an additional charge. You see the estimated delivery time from each vendor before you place your order — no surprises.",
  },
  {
    q: "What happens if I am not happy with the print quality?",
    a: "If your order doesn't match what was agreed, raise a dispute directly in the app within 48 hours of delivery. Our team reviews evidence from both sides and mediates a fair resolution. If the issue is confirmed, you receive a full or partial refund to your EarlyPrint wallet. Your payment is protected by our escrow system until you're satisfied.",
  },
  {
    q: "I am a print vendor. How can I join EarlyPrint and get more orders?",
    a: "Joining EarlyPrint as a vendor is completely free. Sign up, complete your business profile, set your service categories and working hours — and start receiving daily order leads from customers near you. You only pay EarlyPrint a small commission when you complete an order. No upfront cost. No risk.",
  },
];

export default function FAQs() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <section className="border-b border-neutral-200 bg-neutral-950 text-white grain">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
          <span className="text-xs font-semibold uppercase tracking-widest text-lime-400">Frequently asked questions</span>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-black leading-tight tracking-tight md:text-6xl" data-testid="faqs-title">
            Answers before you <span className="text-lime-400">sign up</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-neutral-300">Everything about escrow, live bidding, delivery, disputes and vendor registration — all in one place.</p>
        </div>
      </section>

      <section className="border-b border-neutral-200" data-testid="faqs-list">
        <div className="mx-auto max-w-3xl px-4 py-16 md:px-6 md:py-24">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-neutral-200" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display text-lg font-semibold hover:no-underline" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-neutral-700" data-testid={`faq-content-${i}`}>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}
